import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Star, BadgeDollarSign, MapPinned, Truck, CheckCircle2 } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { NetworkCanvas } from "@/components/site/NetworkCanvas";

const PILLS = [
  { icon: BadgeDollarSign, label: "Very Reasonable Cost" },
  { icon: MapPinned, label: "Onsite Nationwide, USA" },
  { icon: Truck, label: "Deliver On Time" },
];

const fade = (d) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] } });

const HeroVisual = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useMotionValue(200);
  const sy = useMotionValue(200);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 110, damping: 18 });
  const spot = useMotionTemplate`radial-gradient(360px circle at ${sx}px ${sy}px, rgba(41,193,211,0.28), transparent 65%)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    sx.set(e.clientX - r.left);
    sy.set(e.clientY - r.top);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <motion.div
      data-testid="hero-visual"
      onPointerMove={onMove}
      onPointerLeave={reset}
      initial={{ opacity: 0, scale: 0.94, y: 40 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 1.4, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="relative [perspective:1400px]"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="clip-frame relative aspect-[4/3] overflow-hidden rounded-2xl bg-[#14306B] lg:aspect-[4/5]">
          <img src="/images/hero.webp" alt="Bodhatek network operations center with live dispatch maps" className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E2657] via-[#0E2657]/20 to-transparent" />
          <motion.div className="absolute inset-0 mix-blend-screen" style={{ background: spot }} />
        </div>
        <div style={{ transform: "translateZ(70px)" }} className="absolute -left-4 top-8 rounded-xl border border-white/10 bg-[#14306B]/70 p-4 backdrop-blur-xl sm:-left-10">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">Ticket #4821</p>
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-white"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Resolved in 11 min</p>
        </div>
        <div style={{ transform: "translateZ(110px)" }} className="absolute -right-3 bottom-10 rounded-xl border border-white/10 bg-[#14306B]/70 p-4 backdrop-blur-xl sm:-right-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">Field Unit 07</p>
          <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-white">
            <span className="pulse-dot h-2 w-2 rounded-full bg-cyan-400 text-cyan-400" /> On site · ETA met
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yVisual = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden bg-[#0E2657] pb-16 pt-32 sm:pt-36 lg:min-h-[100svh] grain">
      <NetworkCanvas />
      <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-[#1777AF]/25 blur-[140px]" />
      <div className="container-x relative grid items-center gap-14 lg:grid-cols-12">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p {...fade(0)} className="eyebrow flex items-center gap-3 text-cyan-300">
            <span className="h-px w-10 bg-cyan-400" /> Managed IT · Dispatch · Onsite Support
          </motion.p>
          <MaskLines
            onLoad
            as="h1"
            delay={0.1}
            lines={["Managed.", "Dispatched.", { text: "Delivered.", className: "text-grad" }]}
            className="mt-7 font-display text-[3rem] font-extrabold leading-[0.95] tracking-[-0.03em] text-white sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem]"
          />
          <motion.p {...fade(0.6)} className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
            Managed IT, dispatch and onsite support anywhere in the USA — from one partner, at a very reasonable cost. We keep systems up and every job on schedule, so you can run the business.
          </motion.p>
          <motion.div {...fade(0.75)} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/contact?need=it" data-testid="hero-cta-it-support" className="btn-primary">
              I need IT support <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/contact?need=dispatch" data-testid="hero-cta-dispatch" className="btn-ghost border-white/20 text-white hover:border-cyan-300 hover:bg-white/5 hover:text-cyan-300">
              I need dispatch & field service
            </Link>
          </motion.div>
          <motion.div {...fade(0.9)} data-testid="hero-rating-strip" className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-300">
            <span className="flex items-center gap-1 text-cyan-300">
              {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
            </span>
            <span><strong className="text-white">4.9/5</strong> client satisfaction*</span>
            <span className="hidden h-4 w-px bg-white/20 sm:block" />
            <span><strong className="text-white">24/7/365</strong> helpdesk & dispatch desk</span>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yVisual }} className="lg:col-span-5">
          <HeroVisual />
        </motion.div>
      </div>
      <div className="container-x relative mt-16 lg:mt-20">
        <div data-testid="hero-value-pills" className="grid gap-3 sm:grid-cols-3">
          {PILLS.map((p, i) => (
            <motion.div
              key={p.label}
              {...fade(1 + i * 0.1)}
              className="group flex items-center gap-4 rounded-full border border-white/10 bg-white/[0.03] px-5 py-3.5 backdrop-blur-md transition-colors duration-300 hover:border-cyan-400/50"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#1777AF] to-[#29C1D3] text-white transition-transform duration-500 group-hover:rotate-12">
                <p.icon className="h-4 w-4" />
              </span>
              <span className="font-display text-base font-semibold text-white">{p.label}</span>
            </motion.div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-400">*Sample figures for preview — replace with your verified data.</p>
      </div>
    </section>
  );
};
